import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { analysesApi } from "@/services/api";
import type { AnalysisCreate } from "@/types";

/**
 * 분석 상태가 running/pending → completed로 "전환"되는 순간, 차트가 의존하는 쿼리
 * (analysis features/similar-cases + spectrum tic/mzrt/mass)를 한 번 무효화해
 * 자동 refetch시킨다.
 *
 * 배경: useAnalysisDetail은 pending/running 동안 3초 polling으로 상태를 갱신하지만,
 * 차트 데이터 쿼리는 polling이 없어 진입 시 1회만 조회된다. 분석 진행 중 진입하면
 * 빈 응답(회색 그래프)이 캐시되고, 완료 후에도 재조회되지 않아 새로고침 전까지
 * 그래프가 비어 보였다. 이 훅이 완료 전환 시 무효화로 그 간극을 메운다.
 *
 * 처음부터 completed인 경우(전환 아님)에는 무효화하지 않는다(이미 신선하게 조회됨).
 */
export function useRefetchChartsOnComplete(status: string | undefined): void {
  const queryClient = useQueryClient();
  const prevStatus = useRef<string | undefined>(undefined);

  useEffect(() => {
    const was = prevStatus.current;
    prevStatus.current = status;
    if (status === "completed" && was != null && was !== "completed") {
      queryClient.invalidateQueries({ queryKey: ["analysis"] });
      queryClient.invalidateQueries({ queryKey: ["spectrum"] });
    }
  }, [status, queryClient]);
}

export function useAnalysisList(params?: { skip?: number; limit?: number }) {
  return useQuery({
    queryKey: ["analyses", params],
    queryFn: async () => {
      const response = await analysesApi.list(params);
      return response.data;
    },
  });
}

export function useAnalysisDetail(id: number | undefined) {
  return useQuery({
    queryKey: ["analysis", "detail", id],
    queryFn: async () => {
      if (!id) return null;
      const response = await analysesApi.getDetail(id);
      return response.data;
    },
    enabled: !!id,
    // Phase 1: pending/running 상태이면 3초 간격 polling
    refetchInterval: (query) => {
      const s = (query.state.data as { status?: string } | null)?.status;
      return s === "pending" || s === "running" ? 3000 : false;
    },
  });
}

export function useAnalysisFeatures(id: number | undefined) {
  return useQuery({
    queryKey: ["analysis", "features", id],
    queryFn: async () => {
      if (!id) return [];
      const response = await analysesApi.getFeatures(id);
      return response.data.features;
    },
    enabled: !!id,
  });
}

export function useSimilarCases(id: number | undefined) {
  return useQuery({
    queryKey: ["analysis", "similar-cases", id],
    queryFn: async () => {
      if (!id) return [];
      const response = await analysesApi.getSimilarCases(id);
      return response.data.similar_cases;
    },
    enabled: !!id,
  });
}

export function useCreateAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (data: AnalysisCreate) => {
      const response = await analysesApi.create(data);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["analyses"] });
    },
  });
}

export function useAnalysesBySample(sampleId?: number) {
  return useQuery({
    queryKey: ["analyses", "by-sample", sampleId],
    queryFn: async () => {
      if (!sampleId) return [];
      const response = await analysesApi.list({
        sample_id: sampleId,
        limit: 100,
      });
      return response.data;
    },
    enabled: !!sampleId,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (Array.isArray(data)) {
        const hasRunning = data.some(
          (a: { status: string }) =>
            a.status === "running" || a.status === "pending",
        );
        if (hasRunning) return 5000;
      }
      return false;
    },
  });
}

export function useAnalysesByCase(caseId?: number) {
  return useQuery({
    queryKey: ["analyses", "by-case", caseId],
    queryFn: async () => {
      if (!caseId) return [];
      const response = await analysesApi.getByCase(caseId);
      return response.data;
    },
    enabled: !!caseId,
    // B2: 분석 상태 자동 갱신 — running 상태가 있으면 5초 간격 polling
    refetchInterval: (query) => {
      const data = query.state.data;
      if (Array.isArray(data)) {
        const hasRunning = data.some(
          (a: { status: string }) =>
            a.status === "running" || a.status === "pending",
        );
        if (hasRunning) return 5000;
      }
      return false;
    },
  });
}

export function useDeleteAnalysis() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: number) => {
      await analysesApi.delete(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["analyses"] });
    },
  });
}

/** T2-3: 분석 1건의 판정 목록 (현재 + 이력). */
export function useAssessments(analysisId: number | undefined) {
  return useQuery({
    queryKey: ["analysis", "assessments", analysisId],
    queryFn: async () => {
      const response = await analysesApi.getAssessments(analysisId as number);
      return response.data;
    },
    enabled: !!analysisId,
  });
}

/** T2-3: 판정 기록 — 성공 시 판정 목록·features(배지) 갱신. */
export function useCreateAssessment(analysisId: number | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      resultId: number;
      status: string;
      rationale: string;
    }) => {
      const response = await analysesApi.createAssessment(
        analysisId as number,
        input.resultId,
        { status: input.status, rationale: input.rationale },
      );
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["analysis", "assessments", analysisId],
      });
      queryClient.invalidateQueries({
        queryKey: ["analysis", "features", analysisId],
      });
      queryClient.invalidateQueries({
        queryKey: ["analysis", "detail", analysisId],
      });
    },
  });
}
